'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { useLinkStatus } from 'next/link'
import { useEffect, useSyncExternalStore } from 'react'

/**
 * The feedback a navigation gives while it waits for its page: a thin bar along the top of the
 * window (ADR-0017). Every page is prerendered and most links are prefetched, so a click usually
 * lands at once and this shows nothing; it exists for the click that has to fetch first — a slow
 * network, a link that was never in view, a cold CDN cache — where the old page otherwise sat
 * there unchanged and the click looked ignored.
 *
 * Next.js knows which navigation is pending only inside the `<Link>` that started it
 * (`useLinkStatus`), so every internal link wrapper renders a `<LinkPending />`, and the one bar in
 * the layout reads what they report. A link is pending even when its route is prefetched, for as
 * long as the new page takes to render — 26–56ms on a developer laptop, 109ms at 4× CPU slowdown,
 * 329ms at 8× — so the bar is always switched on; the 150ms delay in `globals.css` keeps it
 * invisible on those clicks and on fetches that answer quickly, and lets it show on a device slow
 * enough that the visitor is really waiting.
 */

let pendingLinks = 0
const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const update = (delta: number) => {
  pendingLinks += delta
  listeners.forEach((listener) => listener())
}

/** Reports its enclosing `<Link>`'s pending navigation to the bar. Renders nothing. */
export function LinkPending() {
  const { pending } = useLinkStatus()

  useEffect(() => {
    if (!pending) {
      return
    }
    update(1)
    // Runs when the navigation completes and also when the link unmounts mid-navigation, which
    // the outgoing page's links do as the new page replaces them.
    return () => update(-1)
  }, [pending])

  return null
}

export function NavigationProgress() {
  const active = useSyncExternalStore(
    subscribe,
    () => pendingLinks > 0,
    () => false,
  )

  return <div aria-hidden="true" className="navigation-progress" data-active={active || undefined} />
}
